import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Dialog, {
	DialogContent,
	DialogFooter,
	DialogTop,
	DialogTrigger
} from '@/components/Dialog';
import { Drawer, DrawerContent } from '@/components/Drawer';
import Input from '@/components/Input';
import { RadioGroup, RadioGroupItem } from '@/components/RadioGroup';
import Label from '@/components/form/Label';
import { useMediaQuery } from '@/hooks';
import DOCIcon from '@/icons/doc.svg';
import Download from '@/icons/download.svg';
import JPGIcon from '@/icons/jpg.svg';
import PDFIcon from '@/icons/pdf.svg';
import PNGIcon from '@/icons/png.svg';
import PPTIcon from '@/icons/ppt.svg';
import XLSIcon from '@/icons/xls.svg';
import routes from '@/routes';
import { setIsModalRateUsOpen } from '@/store/myDocuments';
import { cn, showError, showSuccess } from '@/utils';
import {
	checkIfFileNeedsPassword,
	downloadZip,
	genericDownload
} from '@/utils/download';

import ScrollArea from './ScrollArea';
import Spinner from './Spinner';

function DownloadModal({
	open,
	selectedTransactions,
	onOpenChange,
	withTrigger = false
}) {
	const { t } = useTranslation();
	const mdDown = useMediaQuery('mdDown');

	if (mdDown) {
		return (
			<DownloadDrawer
				onOpenChange={onOpenChange}
				open={open}
				selectedTransactions={selectedTransactions}
			/>
		);
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			{withTrigger && (
				<DialogTrigger asChild>
					<Button size="sm" variant="outline" startIcon={<Download />}>
						{t('common.download')}
					</Button>
				</DialogTrigger>
			)}
			<DialogContent className="w-[320px] p-0 md:max-h-[calc(100vh-40px)] md:w-[606px]">
				<DownloadContent
					selectedTransactions={selectedTransactions}
					onOpenChange={onOpenChange}
				/>
			</DialogContent>
		</Dialog>
	);
}

export function DownloadContent({ selectedTransactions, onOpenChange }) {
	const defaultFilename = `${selectedTransactions[0].metadata.originalFileName.replace(/\.\w{3,4}$/, '')}`;

	const [hasPassword, setHasPassword] = useState(false);
	const [loading, setLoading] = useState(true);
	const { t } = useTranslation();
	const mdDown = useMediaQuery('mdDown');
	const [selectedFormat, setSelectedFormat] = useState('');
	const [filename, setFilename] = useState(defaultFilename);
	const [fileDownloading, setFileDownloading] = useState(false);

	const dispatch = useDispatch();
	const isDOC = selectedFormat === 'docx';
	const isEditor = useMatch(routes.editor);
	const isMyDocuments = useMatch(routes.myDocuments);
	const password = useSelector(state => state.editor.password);

	const formats = [
		{
			name: 'pdf',
			label: 'PDF',
			icon: PDFIcon
		},
		{
			name: 'docx',
			label: 'Word',
			icon: DOCIcon
		},
		{
			name: 'xls',
			label: 'Excel',
			icon: XLSIcon
		},
		{
			name: 'jpg',
			label: 'JPG',
			icon: JPGIcon
		},
		{
			name: 'png',
			label: 'PNG',
			icon: PNGIcon
		},
		{
			name: 'ppt',
			label: 'Power Point',
			icon: PPTIcon
		}
	];

	const handleSuccess = () => {
		onOpenChange(false);
		showSuccess(t('myDocuments.toast.downloadDocumentSuccess'));
		if (isMyDocuments) {
			setTimeout(() => {
				dispatch(setIsModalRateUsOpen(true));
			}, 1_000);
		}
	};

	const downloadRow = async format => {
		try {
			setFileDownloading(true);

			if (['jpg', 'png'].includes(format)) {
				await downloadZip(selectedTransactions[0].id, {
					format,
					filename
				});

				handleSuccess();

				return;
			}

			await genericDownload(selectedTransactions[0].id, {
				format,
				filename
			});

			handleSuccess();
		} catch (error) {
			showError(error);
		} finally {
			setFileDownloading(false);
		}
	};

	useEffect(() => {
		if (isEditor) {
			setLoading(false);
			setHasPassword(!!password);
			return;
		}

		const checkFilesForPassword = async () => {
			for (const tx of selectedTransactions) {
				if (await checkIfFileNeedsPassword(tx.id)) {
					setHasPassword(true);
					break;
				}
			}
			setLoading(false);
		};

		checkFilesForPassword();
	}, [selectedTransactions]);

	return (
		<>
			<DialogTop
				title={t('myDocuments.modals.download.title')}
				description={t('myDocuments.modals.download.description')}
				className="text-sm leading-6"
			/>

			<div className="flex flex-col gap-4 p-5">
				{loading ? (
					<Spinner
						className="m-auto h-80 max-h-[calc(80svh-260px)] text-grey-500"
						size={50}
					/>
				) : (
					<>
						{!!hasPassword && (
							<Alert
								message={t('myDocuments.modals.download.alertPDF')}
								variant="info"
							/>
						)}
						{isDOC && (
							<Alert
								message={t('myDocuments.modals.download.alertDOC')}
								variant="info"
							/>
						)}
						<ScrollArea
							className="h-full"
							maxHeight={
								mdDown ? 'calc(90svh - 260px)' : 'calc(80svh - 260px)'
							}
						>
							<RadioGroup
								onValueChange={setSelectedFormat}
								value={selectedFormat}
								className="gap-1"
							>
								{formats.map(format => {
									const CurrentIcon = format.icon;
									const disabled =
										!!hasPassword && format.name !== 'pdf';

									return (
										<label
											key={format.name}
											className={cn(
												'flex w-full cursor-pointer items-center justify-between rounded border border-grey-200 px-5 py-2 text-sm transition-colors focus:border-black focus-visible:outline-none',
												selectedFormat === format.name &&
													'border-black',
												disabled && 'cursor-not-allowed bg-grey-100'
											)}
										>
											<div className="flex items-center gap-3">
												<CurrentIcon
													aria-hidden
													className={cn(
														'h-9 w-9 shrink-0 rounded bg-grey-100 text-black md:h-12 md:w-12',
														selectedFormat === format.name &&
															'bg-black text-red',
														disabled && 'text-grey-400'
													)}
												/>
												<div className="flex flex-col">
													<span
														className={cn(
															'text-xs font-semibold leading-5 text-black md:text-sm md:leading-6',
															disabled && 'text-grey-400'
														)}
													>
														{format.label}
													</span>
													<span
														className={cn(
															'text-[10px] leading-4 text-grey-500 md:text-xs md:leading-5',
															disabled && 'text-grey-400'
														)}
													>
														{t(
															`myDocuments.modals.download.formats.${format.name}.description`
														)}
													</span>
												</div>
											</div>

											<RadioGroupItem
												disabled={disabled}
												value={format.name}
												id={format.name}
												checked={selectedFormat === format.name}
											/>
										</label>
									);
								})}
							</RadioGroup>
						</ScrollArea>
					</>
				)}

				<div>
					<Label htmlFor="download-filename">
						{t('myDocuments.modals.download.labelFileName')}
					</Label>
					<Input
						size={mdDown ? 'default' : 'lg'}
						id="download-filename"
						type="text"
						onChange={e => setFilename(e.target.value)}
						value={filename}
					/>
				</div>
			</div>
			<DialogFooter>
				<Button variant="outline" onClick={() => onOpenChange(false)}>
					{t('common.cancel')}
				</Button>
				<Button
					onClick={() => downloadRow(selectedFormat)}
					disabled={!selectedFormat || !filename}
					loading={fileDownloading}
				>
					{t('common.download')}
				</Button>
			</DialogFooter>
		</>
	);
}

function DownloadDrawer({ onOpenChange, open, selectedTransactions }) {
	return (
		<Drawer open={open} onOpenChange={onOpenChange}>
			<DrawerContent className="p-0 md:w-[606px]">
				<DownloadContent
					onOpenChange={onOpenChange}
					selectedTransactions={selectedTransactions}
				/>
			</DrawerContent>
		</Drawer>
	);
}

export default DownloadModal;
